import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  MessageCircle, Users, Plus, ArrowLeft, Send, Heart, Sparkles, 
  Clock, Eye, ChevronRight, BookOpen, Home, RefreshCw, Loader2,
  ThumbsUp, Star, MoreVertical, Trash2, Edit2, Reply, Play, UserPlus,
  Palette, ExternalLink, Menu, X, Wifi, WifiOff, Search, SortAsc, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { InfinityAlchemyIcon, SpiralIcon, FlameIcon, LotusIcon, CrystalIcon, ScrollIcon, HeartAlchemyIcon } from './icons/AlchemyIcons';
import PremiumFooter from './PremiumFooter';
import EmpathyAlertButton from './EmpathyAlertButton';
import UserMessaging from './UserMessaging';
import CommunityOfferings from './CommunityOfferings';

export default function ForumPage() {
  const navigate = useNavigate();
  const [userId] = useState(() => localStorage.getItem('workbook_user_id'));
  const [userName] = useState(() => localStorage.getItem('workbook_user_name') || 'Anonymous');
  const [userEmail] = useState(() => localStorage.getItem('workbook_user_email') || '');
  
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [discussions, setDiscussions] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showMessaging, setShowMessaging] = useState(false);
  const [forumTab, setForumTab] = useState('discussions'); // 'discussions' or 'gifts'
  
  // Group Sessions
  const [groupSessions, setGroupSessions] = useState([]);
  const [showStartExerciseDialog, setShowStartExerciseDialog] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState('1');
  const [selectedExerciseType, setSelectedExerciseType] = useState('ebook'); // 'ebook' or 'practice'
  const [selectedPracticeType, setSelectedPracticeType] = useState('ofnr'); // 'ofnr', 'empathy', 'self_empathy'
  
  // Form states
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomDescription, setNewRoomDescription] = useState('');
  const [newDiscussionTitle, setNewDiscussionTitle] = useState('');
  const [newDiscussionContent, setNewDiscussionContent] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [showNewRoomDialog, setShowNewRoomDialog] = useState(false);
  const [showNewDiscussionDialog, setShowNewDiscussionDialog] = useState(false);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Online presence states
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isOnline, setIsOnline] = useState(true);
  
  // Sketch project states
  const [showSketchDialog, setShowSketchDialog] = useState(false);
  const [sketchTitle, setSketchTitle] = useState('');
  const [selectedDiscussionForSketch, setSelectedDiscussionForSketch] = useState(null);
  const [creatingSketch, setCreatingSketch] = useState(false);
  
  // Search and sorting states
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'popular', 'active'
  const [showSearch, setShowSearch] = useState(false);

  // Chapter list for exercises
  const chapters = [
    { value: '1', label: 'Chapter 1: The Journey Begins' },
    { value: '2', label: 'Chapter 2: The Camera Lens' },
    { value: '3', label: 'Chapter 3: Feelings & Needs' },
    { value: '4', label: 'Chapter 4: Making Requests' },
    { value: '5', label: 'Chapter 5: Empathy in Action' },
    { value: '6', label: 'Chapter 6: Self-Compassion' },
    { value: '7', label: 'Chapter 7: Integration' },
  ];

  // Fetch rooms on mount
  useEffect(() => {
    fetchRooms();
  }, []);

  // Fetch group sessions when room selected
  useEffect(() => {
    if (selectedRoom) {
      fetchGroupSessions(selectedRoom._id);
    }
  }, [selectedRoom]);

  const fetchGroupSessions = async (roomId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-sessions/room/${roomId}`);
      if (response.ok) {
        const data = await response.json();
        setGroupSessions(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch group sessions:', error);
    }
  };

  const startGroupExercise = async () => {
    if (!userId) {
      toast.error('Please log in to start an exercise');
      navigate('/login');
      return;
    }

    try {
      if (selectedExerciseType === 'practice') {
        // Create a Group Practice session with user-labeled fields
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-practice/sessions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': userId,
            'X-User-Name': userName
          },
          body: JSON.stringify({
            title: `${selectedRoom?.name || 'Group'} Practice - ${selectedPracticeType.toUpperCase()}`,
            description: `Group practice session for ${selectedRoom?.name || 'the community'}`,
            exercise_type: selectedPracticeType,
            chapter: parseInt(selectedChapter),
            room_id: selectedRoom._id
          })
        });

        if (response.ok) {
          const data = await response.json();
          toast.success('Group practice started!');
          setShowStartExerciseDialog(false);
          // Navigate to the new Group Practice page
          navigate(`/group-practice?session=${data.session._id}`);
        } else {
          throw new Error('Failed to start practice session');
        }
      } else {
        // Original eBook group exercise
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-sessions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': userId,
            'X-User-Name': userName
          },
          body: JSON.stringify({
            room_id: selectedRoom._id,
            chapter: parseInt(selectedChapter),
            exercise_type: 'general'
          })
        });

        if (response.ok) {
          const data = await response.json();
          toast.success('Group exercise started!');
          setShowStartExerciseDialog(false);
          // Navigate to the workbook with group session
          navigate(`/belonging-ebook?chapter=${selectedChapter}&group=${data.session._id}`);
        } else {
          throw new Error('Failed to start session');
        }
      }
    } catch (error) {
      toast.error('Failed to start group exercise');
    }
  };

  const joinGroupSession = async (sessionId, chapter) => {
    if (!userId) {
      toast.error('Please log in to join');
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-sessions/${sessionId}/join`, {
        method: 'POST',
        headers: {
          'X-User-Id': userId,
          'X-User-Name': userName
        }
      });

      if (response.ok) {
        toast.success('Joined group session!');
        navigate(`/belonging-ebook?chapter=${chapter}&group=${sessionId}`);
      } else {
        throw new Error('Failed to join');
      }
    } catch (error) {
      toast.error('Failed to join session');
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forum/rooms`);
      if (response.ok) {
        const data = await response.json();
        setRooms(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
      toast.error('Failed to load forum rooms');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomDetails = async (roomId) => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forum/rooms/${roomId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedRoom(data.room);
        setDiscussions(data.discussions || []);
      }
    } catch (error) {
      toast.error('Failed to load room');
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscussionPosts = async (discussionId) => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forum/discussions/${discussionId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedDiscussion(data.discussion);
        setPosts(data.posts || []);
      }
    } catch (error) {
      toast.error('Failed to load discussion');
    } finally {
      setLoading(false);
    }
  };

  const createRoom = async () => {
    if (!userId) {
      toast.error('Please log in to create a room');
      navigate('/login');
      return;
    }
    
    if (!newRoomName.trim()) {
      toast.error('Please enter a room name');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forum/rooms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
          'X-User-Name': userName
        },
        body: JSON.stringify({
          name: newRoomName,
          description: newRoomDescription,
          room_type: 'general'
        })
      });

      if (response.ok) {
        toast.success('Room created!');
        setNewRoomName('');
        setNewRoomDescription('');
        setShowNewRoomDialog(false);
        fetchRooms();
      } else {
        throw new Error('Failed to create room');
      }
    } catch (error) {
      toast.error('Failed to create room');
    }
  };

  const createDiscussion = async () => {
    if (!userId) {
      toast.error('Please log in to start a discussion');
      navigate('/login');
      return;
    }

    if (!newDiscussionTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forum/discussions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
          'X-User-Name': userName
        },
        body: JSON.stringify({
          room_id: selectedRoom._id,
          title: newDiscussionTitle,
          content: newDiscussionContent
        })
      });

      if (response.ok) {
        toast.success('Discussion started!');
        setNewDiscussionTitle('');
        setNewDiscussionContent('');
        setShowNewDiscussionDialog(false);
        fetchRoomDetails(selectedRoom._id);
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Failed to create discussion');
    }
  };

  const createPost = async () => {
    if (!userId) {
      toast.error('Please log in to post');
      navigate('/login');
      return;
    }

    if (!newPostContent.trim()) {
      toast.error('Please enter your message');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forum/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
          'X-User-Name': userName
        },
        body: JSON.stringify({
          discussion_id: selectedDiscussion._id,
          content: newPostContent
        })
      });

      if (response.ok) {
        setNewPostContent('');
        fetchDiscussionPosts(selectedDiscussion._id);
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Failed to post');
    }
  };

  const reactToPost = async (postId, reaction) => {
    if (!userId) {
      toast.error('Please log in to react');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forum/posts/${postId}/react`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId
        },
        body: JSON.stringify({ reaction })
      });

      if (response.ok) {
        fetchDiscussionPosts(selectedDiscussion._id);
      }
    } catch (error) {
      console.error('Failed to react');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forum/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'X-User-Id': userId
        }
      });

      if (response.ok) {
        toast.success('Post deleted');
        fetchDiscussionPosts(selectedDiscussion._id);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const goBack = () => {
    if (selectedDiscussion) {
      setSelectedDiscussion(null);
      setPosts([]);
    } else if (selectedRoom) {
      setSelectedRoom(null);
      setDiscussions([]);
    } else {
      navigate(userId ? '/dashboard' : '/');
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    if (selectedDiscussion) {
      await fetchDiscussionPosts(selectedDiscussion._id);
    } else if (selectedRoom) {
      await fetchRoomDetails(selectedRoom._id);
    } else {
      await fetchRooms();
    }
    setRefreshing(false);
  };

  // Online presence tracking
  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  // Simulated online users for multi-user engagement
  useEffect(() => {
    if (selectedRoom) {
      // Simulate online users - in production this would come from websocket/polling
      const simulatedUsers = [
        { id: '1', name: 'Sarah', avatar: 'S', active: true },
        { id: '2', name: 'Michael', avatar: 'M', active: true },
        { id: '3', name: 'Emma', avatar: 'E', active: false },
      ];
      if (userId) {
        simulatedUsers.unshift({ id: userId, name: userName, avatar: userName?.charAt(0) || '?', active: true, isYou: true });
      }
      setOnlineUsers(simulatedUsers);
    }
  }, [selectedRoom, userId, userName]);

  // Create Sketch Project on ElchmE.com
  const createSketchProject = (discussion) => {
    setSelectedDiscussionForSketch(discussion);
    setSketchTitle(discussion?.title || '');
    setShowSketchDialog(true);
  };

  const launchSketchProject = async () => {
    if (!userId) {
      toast.error('Please log in to create a sketch project');
      navigate('/login');
      return;
    }
    
    setCreatingSketch(true);
    
    try {
      // Generate secure SSO link using ElchmE's system
      const user = {
        id: userId,
        name: userName,
        email: userEmail || '',
        roles: ['user'],
        context: {
          from: 'forum_sketch',
          sketch_title: sketchTitle || selectedDiscussionForSketch?.title,
          discussion_id: selectedDiscussionForSketch?._id,
          discussion_title: selectedDiscussionForSketch?.title,
          room_name: selectedRoom?.name
        }
      };
      
      const elchmeUrl = await generateElchmEResearchLink(user);
      
      toast.success('Opening ElchmE.com with your sketch...');
      window.open(elchmeUrl, '_blank', 'noopener,noreferrer');
      
      setShowSketchDialog(false);
      setSketchTitle('');
      setSelectedDiscussionForSketch(null);
      
    } catch (error) {
      console.error('Failed to launch ElchmE:', error);
      toast.error('Failed to generate secure link. Please try again.');
    } finally {
      setCreatingSketch(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Filter and sort rooms
  const filteredRooms = rooms
    .filter(room => 
      searchQuery === '' || 
      room.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (room.description && room.description.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'oldest': return new Date(a.created_at) - new Date(b.created_at);
        case 'popular': return (b.discussion_count || 0) - (a.discussion_count || 0);
        case 'active': return new Date(b.last_activity || b.created_at) - new Date(a.last_activity || a.created_at);
        default: return new Date(b.created_at) - new Date(a.created_at); // newest
      }
    });

  // Filter and sort discussions
  const filteredDiscussions = discussions
    .filter(disc => 
      searchQuery === '' || 
      disc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (disc.content && disc.content.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'oldest': return new Date(a.created_at) - new Date(b.created_at);
        case 'popular': return (b.post_count || 0) - (a.post_count || 0);
        case 'active': return new Date(b.last_activity || b.created_at) - new Date(a.last_activity || a.created_at);
        default: return new Date(b.created_at) - new Date(a.created_at); // newest
      }
    });

  // Room type icons
  const getRoomIcon = (type) => {
    switch (type) {
      case 'chapter': return <BookOpen className="w-5 h-5" />;
      case 'exercise': return <FlameIcon className="w-5 h-5" />;
      default: return <MessageCircle className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header - Mobile Optimized */}
      <header className="relative overflow-hidden border-b border-teal-500/20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(45, 212, 191, 0.4) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
          {/* Mobile Header */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-4">
              <button 
                onClick={goBack}
                className="text-stone-500 hover:text-white transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                data-testid="forum-back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {selectedDiscussion ? 'Back to Room' : selectedRoom ? 'All Rooms' : (userId ? 'Dashboard' : 'Home')}
                </span>
              </button>
              
              {/* Online Status Indicator */}
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${isOnline ? 'bg-teal-500/20 text-teal-400' : 'bg-red-500/20 text-red-400'}`}>
                {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                <span className="hidden sm:inline">{isOnline ? 'Online' : 'Offline'}</span>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-3">
              {/* Instant Empathy Alert Button */}
              {userId && (
                <EmpathyAlertButton variant="compact" />
              )}
              
              {/* Messages Button */}
              {userId && (
                <Button
                  onClick={() => setShowMessaging(true)}
                  variant="ghost"
                  className="text-stone-400 hover:text-amber-400"
                  data-testid="forum-messages-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                </Button>
              )}
              
              {/* Search Toggle */}
              <Button
                onClick={() => setShowSearch(!showSearch)}
                variant="ghost"
                className={`text-stone-500 hover:text-teal-400 ${showSearch ? 'text-teal-400' : ''}`}
                data-testid="forum-search-toggle"
              >
                <Search className="w-4 h-4" />
              </Button>
              
              <Button
                onClick={refreshData}
                variant="ghost"
                className="text-stone-500 hover:text-teal-400"
                data-testid="forum-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              
              {userId && (
                <a href="https://ElchmE.com" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-transparent border border-amber-500/30 text-amber-500 hover:bg-amber-500/10 rounded-none px-3" data-testid="elchme-link">
                    <InfinityAlchemyIcon className="w-4 h-4 mr-2" />
                    ElchmE
                  </Button>
                </a>
              )}
              
              <a href="/belonging-ebook">
                <Button className="bg-transparent border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 rounded-none" data-testid="ebook-link">
                  <BookOpen className="w-4 h-4 mr-2" />
                  eBook
                </Button>
              </a>
            </div>
            
            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden p-2 text-stone-400 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden absolute left-0 right-0 top-full bg-stone-900/95 backdrop-blur-md border-b border-stone-800 p-4 z-50">
              <div className="flex flex-col gap-2">
                {userId && (
                  <EmpathyAlertButton variant="default" className="w-full justify-center" />
                )}
                
                {userId && (
                  <Button
                    onClick={() => { setShowMessaging(true); setMobileMenuOpen(false); }}
                    className="w-full justify-start bg-transparent text-amber-400 hover:bg-amber-500/10"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Messages
                  </Button>
                )}
                
                <Button
                  onClick={() => { refreshData(); setMobileMenuOpen(false); }}
                  variant="ghost"
                  className="justify-start text-stone-400 hover:text-white"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                
                {userId && (
                  <a href="https://ElchmE.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full justify-start bg-transparent text-amber-500 hover:bg-amber-500/10">
                      <InfinityAlchemyIcon className="w-4 h-4 mr-2" />
                      ElchmE.com
                    </Button>
                  </a>
                )}
                
                <a href="/belonging-ebook" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full justify-start bg-transparent text-teal-400 hover:bg-teal-500/10">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Belonging eBook
                  </Button>
                </a>
                
                <a href="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full justify-start bg-transparent text-stone-400 hover:bg-stone-800">
                    <Home className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>
                </a>
              </div>
            </div>
          )}

          <div className="mt-6 sm:mt-8 text-center">
            <p className="text-teal-500 text-xs tracking-[0.3em] uppercase mb-2 sm:mb-4">Community</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-wide mb-2 sm:mb-4 flex items-center justify-center gap-2 sm:gap-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              <SpiralIcon className="w-8 h-8 sm:w-10 sm:h-10 text-teal-400" />
              FORUM
            </h1>
            <p className="text-stone-400 text-sm sm:text-lg font-light max-w-xl mx-auto px-4">
              {selectedDiscussion 
                ? selectedDiscussion.title
                : selectedRoom 
                  ? selectedRoom.name 
                  : 'Connect, discuss, and grow together in compassionate dialogue'}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
          </div>
        ) : !selectedRoom ? (
          // Room List View
          <div className="space-y-6 sm:space-y-8">
            {/* Search and Sort Bar */}
            {(showSearch || rooms.length > 3) && (
              <div className="flex flex-col sm:flex-row gap-3 bg-stone-900/50 border border-stone-800 rounded-lg p-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search rooms..."
                    className="pl-10 bg-stone-800 border-stone-700 text-white"
                    data-testid="forum-search-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-32 bg-stone-800 border-stone-700 text-white">
                      <SortAsc className="w-3 h-3 mr-1" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-stone-800 border-stone-700">
                      <SelectItem value="newest" className="text-white hover:bg-stone-700">Newest</SelectItem>
                      <SelectItem value="oldest" className="text-white hover:bg-stone-700">Oldest</SelectItem>
                      <SelectItem value="popular" className="text-white hover:bg-stone-700">Most Popular</SelectItem>
                      <SelectItem value="active" className="text-white hover:bg-stone-700">Most Active</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              {/* Forum Tabs */}
              <Tabs value={forumTab} onValueChange={setForumTab} className="w-full">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <TabsList className="bg-stone-800/50 border border-stone-700">
                    <TabsTrigger 
                      value="discussions" 
                      className="data-[state=active]:bg-teal-600 data-[state=active]:text-white"
                      data-testid="discussions-tab"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Discussions
                    </TabsTrigger>
                    <TabsTrigger 
                      value="gifts" 
                      className="data-[state=active]:bg-amber-600 data-[state=active]:text-white"
                      data-testid="gifts-tab"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Community Gifts
                    </TabsTrigger>
                  </TabsList>
                  
                  {forumTab === 'discussions' && (
                    <Dialog open={showNewRoomDialog} onOpenChange={setShowNewRoomDialog}>
                      <DialogTrigger asChild>
                        <Button 
                          className="w-full sm:w-auto bg-teal-600 hover:bg-teal-500 text-white rounded-none"
                          data-testid="create-room-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          New Room
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-stone-900 border-stone-700 text-white mx-4 sm:mx-auto max-w-[calc(100vw-2rem)] sm:max-w-lg">
                        <DialogHeader>
                          <DialogTitle className="text-xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                            Create Discussion Room
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <label className="text-sm text-stone-400 mb-2 block">Room Name</label>
                            <Input
                              value={newRoomName}
                              onChange={(e) => setNewRoomName(e.target.value)}
                              placeholder="e.g., Chapter 2 Study Group"
                              className="bg-stone-800 border-stone-700 text-white"
                              data-testid="room-name-input"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-stone-400 mb-2 block">Description (optional)</label>
                            <Textarea
                              value={newRoomDescription}
                              onChange={(e) => setNewRoomDescription(e.target.value)}
                              placeholder="What will this room be about?"
                              className="bg-stone-800 border-stone-700 text-white min-h-[80px]"
                              data-testid="room-description-input"
                      />
                    </div>
                    <Button 
                      onClick={createRoom} 
                      className="w-full bg-teal-600 hover:bg-teal-500 text-white"
                      data-testid="submit-room-btn"
                    >
                      Create Room
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
                  )}
                </div>
                
                {/* Discussions TabsContent */}
                <TabsContent value="discussions" className="mt-0">
                  {searchQuery && (
                    <p className="text-stone-500 text-sm mb-4">
                      {filteredRooms.length} results for "{searchQuery}"
                    </p>
                  )}

            {rooms.length === 0 ? (
              <div className="text-center py-12 sm:py-20 bg-stone-900/30 border border-stone-800 rounded-lg px-4">
                <LotusIcon className="w-12 sm:w-16 h-12 sm:h-16 text-stone-600 mx-auto mb-4" />
                <p className="text-stone-400 text-base sm:text-lg mb-2">No discussion rooms yet</p>
                <p className="text-stone-500 text-sm mb-6">Be the first to create a space for connection</p>
                <Button 
                  onClick={() => setShowNewRoomDialog(true)}
                  className="bg-teal-600 hover:bg-teal-500 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Room
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {filteredRooms.map((room) => (
                  <button
                    key={room._id}
                    onClick={() => fetchRoomDetails(room._id)}
                    className="text-left bg-stone-900/50 border border-stone-800 rounded-lg p-4 sm:p-6 hover:border-teal-500/50 hover:bg-stone-900 transition-all group"
                    data-testid={`room-card-${room._id}`}
                  >
                    <div className="flex items-start justify-between mb-3 sm:mb-4">
                      <div className="p-2 rounded-lg bg-teal-500/10 text-teal-400">
                        {getRoomIcon(room.room_type)}
                      </div>
                      <Badge variant="secondary" className="bg-stone-800 text-stone-400 text-xs">
                        {room.discussion_count || 0} discussions
                      </Badge>
                    </div>
                    
                    <h3 className="text-base sm:text-lg text-white font-medium mb-2 group-hover:text-teal-400 transition-colors line-clamp-1">
                      {room.name}
                    </h3>
                    
                    {room.description && (
                      <p className="text-stone-500 text-sm mb-3 sm:mb-4 line-clamp-2">
                        {room.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between text-stone-600 text-xs">
                      <span className="flex items-center gap-1 truncate">
                        <Users className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">by {room.created_by_name}</span>
                      </span>
                      <ChevronRight className="w-4 h-4 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                ))}
              </div>
            )}
                </TabsContent>
                
                {/* Community Gifts TabsContent */}
                <TabsContent value="gifts" className="mt-0">
                  <CommunityOfferings />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        ) : !selectedDiscussion ? (
          // Discussion List View
          <div className="space-y-6 sm:space-y-8">
            {/* Online Users Section */}
            {onlineUsers.length > 0 && (
              <div className="bg-stone-900/30 border border-stone-800 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-teal-400" />
                  <span className="text-sm text-stone-400">People in this room</span>
                  <Badge className="bg-teal-500/20 text-teal-400 text-xs">{onlineUsers.filter(u => u.active).length} online</Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  {onlineUsers.map((user) => (
                    <div 
                      key={user.id}
                      className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs ${user.isYou ? 'bg-teal-500/20 text-teal-300' : 'bg-stone-800 text-stone-400'}`}
                    >
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${user.active ? 'bg-teal-500 text-white' : 'bg-stone-600 text-stone-400'}`}>
                        {user.avatar}
                      </div>
                      <span>{user.name}{user.isYou ? ' (you)' : ''}</span>
                      {user.active && <div className="w-2 h-2 rounded-full bg-green-500"></div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {selectedRoom.name}
                </h2>
                {selectedRoom.description && (
                  <p className="text-stone-500 text-sm mt-1">{selectedRoom.description}</p>
                )}
              </div>
              
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                {/* Start Exercise Button */}
                <Dialog open={showStartExerciseDialog} onOpenChange={setShowStartExerciseDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      className="flex-1 sm:flex-none bg-amber-600 hover:bg-amber-500 text-white rounded-none text-sm"
                      data-testid="start-exercise-btn"
                    >
                      <Play className="w-4 h-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Start </span>Exercise
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-stone-900 border-stone-700 text-white mx-4 sm:mx-auto max-w-[calc(100vw-2rem)] sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-light flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                        <CrystalIcon className="w-6 h-6 text-amber-400" />
                        Start Group Session
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <p className="text-stone-400 text-sm">
                        Start a collaborative session where all room members can participate together.
                      </p>
                      
                      {/* Exercise Type Selection */}
                      <div>
                        <label className="text-sm text-stone-400 mb-2 block">Session Type</label>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => setSelectedExerciseType('ebook')}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              selectedExerciseType === 'ebook'
                                ? 'border-amber-500 bg-amber-500/10'
                                : 'border-stone-700 hover:border-stone-600'
                            }`}
                          >
                            <BookOpen className={`w-5 h-5 mb-1 ${selectedExerciseType === 'ebook' ? 'text-amber-400' : 'text-stone-500'}`} />
                            <p className="text-sm font-medium text-white">eBook Exercise</p>
                            <p className="text-xs text-stone-500">Follow chapter content</p>
                          </button>
                          <button
                            onClick={() => setSelectedExerciseType('practice')}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              selectedExerciseType === 'practice'
                                ? 'border-teal-500 bg-teal-500/10'
                                : 'border-stone-700 hover:border-stone-600'
                            }`}
                          >
                            <Users className={`w-5 h-5 mb-1 ${selectedExerciseType === 'practice' ? 'text-teal-400' : 'text-stone-500'}`} />
                            <p className="text-sm font-medium text-white">Group Practice</p>
                            <p className="text-xs text-stone-500">User-labeled inputs</p>
                          </button>
                        </div>
                      </div>
                      
                      {selectedExerciseType === 'ebook' ? (
                        <div>
                          <label className="text-sm text-stone-400 mb-2 block">Select Chapter</label>
                          <Select value={selectedChapter} onValueChange={setSelectedChapter}>
                            <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                              <SelectValue placeholder="Select a chapter" />
                            </SelectTrigger>
                            <SelectContent className="bg-stone-800 border-stone-700">
                              {chapters.map((ch) => (
                                <SelectItem key={ch.value} value={ch.value} className="text-white hover:bg-stone-700">
                                  {ch.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div>
                          <label className="text-sm text-stone-400 mb-2 block">Practice Type</label>
                          <Select value={selectedPracticeType} onValueChange={setSelectedPracticeType}>
                            <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                              <SelectValue placeholder="Select practice type" />
                            </SelectTrigger>
                            <SelectContent className="bg-stone-800 border-stone-700">
                              <SelectItem value="ofnr" className="text-white hover:bg-stone-700">
                                OFNR Practice - Observation, Feeling, Need, Request
                              </SelectItem>
                              <SelectItem value="empathy" className="text-white hover:bg-stone-700">
                                Empathy Practice - Listen & Reflect
                              </SelectItem>
                              <SelectItem value="self_empathy" className="text-white hover:bg-stone-700">
                                Self-Empathy - Connect with yourself
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-stone-500 mt-2">
                            Each participant's responses will be labeled with their name for remote collaboration.
                          </p>
                        </div>
                      )}
                      
                      <Button 
                        onClick={startGroupExercise} 
                        className={`w-full text-white ${
                          selectedExerciseType === 'practice' 
                            ? 'bg-teal-600 hover:bg-teal-500' 
                            : 'bg-amber-600 hover:bg-amber-500'
                        }`}
                        data-testid="submit-start-exercise-btn"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        {selectedExerciseType === 'practice' ? 'Start Group Practice' : 'Start Group Session'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                
                {/* New Discussion Button */}
                <Dialog open={showNewDiscussionDialog} onOpenChange={setShowNewDiscussionDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      className="flex-1 sm:flex-none bg-teal-600 hover:bg-teal-500 text-white rounded-none text-sm"
                      data-testid="create-discussion-btn"
                    >
                      <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">New </span>Discussion
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-stone-900 border-stone-700 text-white mx-4 sm:mx-auto max-w-[calc(100vw-2rem)] sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Start a Discussion
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="text-sm text-stone-400 mb-2 block">Title</label>
                        <Input
                          value={newDiscussionTitle}
                          onChange={(e) => setNewDiscussionTitle(e.target.value)}
                          placeholder="What would you like to discuss?"
                          className="bg-stone-800 border-stone-700 text-white"
                          data-testid="discussion-title-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-stone-400 mb-2 block">Opening Message (optional)</label>
                        <Textarea
                          value={newDiscussionContent}
                          onChange={(e) => setNewDiscussionContent(e.target.value)}
                          placeholder="Share your thoughts to start the conversation..."
                          className="bg-stone-800 border-stone-700 text-white min-h-[120px]"
                          data-testid="discussion-content-input"
                        />
                      </div>
                      <Button 
                        onClick={createDiscussion} 
                        className="w-full bg-teal-600 hover:bg-teal-500 text-white"
                        data-testid="submit-discussion-btn"
                      >
                        Start Discussion
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Active Group Sessions */}
            {groupSessions.length > 0 && (
              <div className="bg-gradient-to-r from-amber-900/20 to-stone-900/50 border border-amber-500/30 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CrystalIcon className="w-5 h-5 text-amber-400" />
                  <h3 className="text-lg font-medium text-amber-200">Active Group Sessions</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {groupSessions.map((session) => (
                    <div 
                      key={session._id}
                      className="bg-stone-900/60 border border-amber-500/20 rounded-lg p-4 flex items-center justify-between"
                    >
                      <div>
                        <p className="text-white font-medium">Chapter {session.chapter} Exercise</p>
                        <p className="text-stone-500 text-xs">
                          Started by {session.created_by_name} • {session.participants?.length || 1} participant{(session.participants?.length || 1) !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <Button
                        onClick={() => joinGroupSession(session._id, session.chapter)}
                        className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30"
                        size="sm"
                        data-testid={`join-session-${session._id}`}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Join
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filteredDiscussions.length === 0 ? (
              <div className="text-center py-12 sm:py-16 bg-stone-900/30 border border-stone-800 rounded-lg px-4">
                <MessageCircle className="w-10 sm:w-12 h-10 sm:h-12 text-stone-600 mx-auto mb-4" />
                <p className="text-stone-400 mb-2">No discussions yet in this room</p>
                <p className="text-stone-500 text-sm mb-4">Start a conversation!</p>
                <Button 
                  onClick={() => setShowNewDiscussionDialog(true)}
                  className="bg-teal-600 hover:bg-teal-500 text-white"
                >
                  Start First Discussion
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredDiscussions.map((discussion) => (
                  <div
                    key={discussion._id}
                    className="bg-stone-900/50 border border-stone-800 rounded-lg p-4 sm:p-5 hover:border-teal-500/50 hover:bg-stone-900 transition-all group"
                    data-testid={`discussion-${discussion._id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <button
                        onClick={() => fetchDiscussionPosts(discussion._id)}
                        className="flex-1 text-left"
                      >
                        <h3 className="text-white font-medium mb-2 group-hover:text-teal-400 transition-colors">
                          {discussion.title}
                        </h3>
                        {discussion.content && (
                          <p className="text-stone-500 text-sm mb-3 line-clamp-2">
                            {discussion.content}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-stone-600 text-xs">
                          <span>by {discussion.created_by_name}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(discussion.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {discussion.post_count || 0} replies
                          </span>
                          <span className="hidden sm:flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {discussion.view_count || 0} views
                          </span>
                        </div>
                      </button>
                      
                      {/* Actions - Sketch Project & View */}
                      <div className="flex items-center gap-2 self-end sm:self-start">
                        <Button
                          onClick={(e) => { e.stopPropagation(); createSketchProject(discussion); }}
                          variant="ghost"
                          size="sm"
                          className="text-amber-500 hover:text-amber-400 hover:bg-amber-500/10 text-xs px-2"
                          data-testid={`sketch-btn-${discussion._id}`}
                        >
                          <Palette className="w-3 h-3 mr-1" />
                          <span className="hidden sm:inline">Sketch</span>
                        </Button>
                        <Button
                          onClick={() => fetchDiscussionPosts(discussion._id)}
                          variant="ghost"
                          size="sm"
                          className="text-teal-400 hover:bg-teal-500/10"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Post/Thread View - Mobile Optimized
          <div className="space-y-4 sm:space-y-6">
            {/* Discussion Header */}
            <div className="bg-stone-900/50 border border-stone-800 rounded-lg p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-2">
                <h2 className="text-lg sm:text-xl text-white font-medium">
                  {selectedDiscussion.title}
                </h2>
                {/* Sketch Project Button in Thread View */}
                <Button
                  onClick={() => createSketchProject(selectedDiscussion)}
                  variant="outline"
                  size="sm"
                  className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10 self-start"
                  data-testid="thread-sketch-btn"
                >
                  <Palette className="w-3 h-3 mr-1" />
                  Create Sketch
                </Button>
              </div>
              {selectedDiscussion.content && (
                <p className="text-stone-400 text-sm sm:text-base mb-4">{selectedDiscussion.content}</p>
              )}
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-stone-500 text-xs sm:text-sm">
                <span>by {selectedDiscussion.created_by_name}</span>
                <span className="hidden sm:inline">•</span>
                <span>{formatDate(selectedDiscussion.created_at)}</span>
                <span className="hidden sm:inline">•</span>
                <span className="flex items-center gap-1">
                  <Eye className="w-3 sm:w-4 h-3 sm:h-4" />
                  {selectedDiscussion.view_count} views
                </span>
              </div>
            </div>

            {/* Posts */}
            <ScrollArea className="h-[350px] sm:h-[400px] pr-2 sm:pr-4">
              <div className="space-y-3 sm:space-y-4">
                {posts.length === 0 ? (
                  <div className="text-center py-8 sm:py-10 text-stone-500">
                    <MessageCircle className="w-8 sm:w-10 h-8 sm:h-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm sm:text-base">No replies yet. Be the first to respond!</p>
                  </div>
                ) : (
                  posts.map((post) => (
                    <div 
                      key={post._id}
                      className="bg-stone-900/30 border border-stone-800 rounded-lg p-3 sm:p-4"
                      data-testid={`post-${post._id}`}
                    >
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white text-xs sm:text-sm font-bold flex-shrink-0">
                            {post.created_by_name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="text-white text-xs sm:text-sm font-medium">{post.created_by_name}</p>
                            <p className="text-stone-600 text-[10px] sm:text-xs">
                              {formatDate(post.created_at)}
                              {post.edited_at && ' (edited)'}
                            </p>
                          </div>
                        </div>
                        
                        {post.created_by === userId && (
                          <button
                            onClick={() => deletePost(post._id)}
                            className="text-stone-600 hover:text-red-400 transition-colors p-1"
                            title="Delete post"
                          >
                            <Trash2 className="w-3 sm:w-4 h-3 sm:h-4" />
                          </button>
                        )}
                      </div>
                      
                      <p className="text-stone-300 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                        {post.content}
                      </p>
                      
                      {/* Reactions - Touch Friendly */}
                      <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                        <button
                          onClick={() => reactToPost(post._id, 'heart')}
                          className={`flex items-center gap-1 px-2 py-1.5 sm:py-1 rounded text-xs transition-colors ${
                            post.reactions?.heart?.includes(userId)
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-stone-800 text-stone-500 hover:text-red-400'
                          }`}
                        >
                          <Heart className="w-3 h-3" />
                          {post.reactions?.heart?.length || 0}
                        </button>
                        <button
                          onClick={() => reactToPost(post._id, 'thumbsup')}
                          className={`flex items-center gap-1 px-2 py-1.5 sm:py-1 rounded text-xs transition-colors ${
                            post.reactions?.thumbsup?.includes(userId)
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-stone-800 text-stone-500 hover:text-blue-400'
                          }`}
                        >
                          <ThumbsUp className="w-3 h-3" />
                          {post.reactions?.thumbsup?.length || 0}
                        </button>
                        <button
                          onClick={() => reactToPost(post._id, 'sparkles')}
                          className={`flex items-center gap-1 px-2 py-1.5 sm:py-1 rounded text-xs transition-colors ${
                            post.reactions?.sparkles?.includes(userId)
                              ? 'bg-amber-500/20 text-amber-400'
                              : 'bg-stone-800 text-stone-500 hover:text-amber-400'
                          }`}
                        >
                          <Sparkles className="w-3 h-3" />
                          {post.reactions?.sparkles?.length || 0}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>

            {/* Reply Input - Mobile Optimized */}
            <div className="bg-stone-900/50 border border-stone-800 rounded-lg p-3 sm:p-4">
              <Textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder="Share your thoughts..."
                className="bg-stone-800 border-stone-700 text-white min-h-[80px] sm:min-h-[100px] mb-3 text-sm"
                data-testid="reply-input"
              />
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <p className="text-stone-600 text-xs">
                  Posting as {userName}
                </p>
                <Button 
                  onClick={createPost}
                  className="w-full sm:w-auto bg-teal-600 hover:bg-teal-500 text-white"
                  disabled={!newPostContent.trim()}
                  data-testid="submit-reply-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Reply
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Sketch Project Dialog */}
      <Dialog open={showSketchDialog} onOpenChange={setShowSketchDialog}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white mx-4 sm:mx-auto max-w-[calc(100vw-2rem)] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-light flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              <Palette className="w-6 h-6 text-amber-400" />
              Create Sketch Project
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-stone-400 text-sm">
              Transform this discussion into a visual sketch project on ElchmE.com. Your ideas will be ready to explore and develop further.
            </p>
            
            <div className="bg-stone-800/50 border border-stone-700 rounded-lg p-4">
              <p className="text-xs text-stone-500 mb-1">Source Discussion</p>
              <p className="text-white font-medium">{selectedDiscussionForSketch?.title}</p>
              {selectedRoom && (
                <p className="text-stone-500 text-xs mt-1">from {selectedRoom.name}</p>
              )}
            </div>
            
            <div>
              <label className="text-sm text-stone-400 mb-2 block">Project Title</label>
              <Input
                value={sketchTitle}
                onChange={(e) => setSketchTitle(e.target.value)}
                placeholder="Name your sketch project..."
                className="bg-stone-800 border-stone-700 text-white"
                data-testid="sketch-title-input"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <Button 
                onClick={launchSketchProject}
                className="flex-1 bg-amber-600 hover:bg-amber-500 text-white"
                data-testid="launch-sketch-btn"
                disabled={creatingSketch}
              >
                {creatingSketch ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                {creatingSketch ? 'Creating Project...' : 'Open in ElchmE.com'}
              </Button>
              <Button 
                onClick={() => setShowSketchDialog(false)}
                variant="outline"
                className="border-stone-600 text-stone-400 hover:text-white"
              >
                Cancel
              </Button>
            </div>
            
            <p className="text-stone-600 text-xs text-center">
              ElchmE.com is our sister platform for visual thinking and creative exploration
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <PremiumFooter />
      
      {/* User Messaging Dialog */}
      <UserMessaging isOpen={showMessaging} onClose={() => setShowMessaging(false)} />
    </div>
  );
}
